import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { research, parsePositions, positionsFromRows, type PositionList } from "../lib/research";

// Saved lists of the roles to research. Research picks one of these.
export default function Roles() {
  const [lists, setLists] = useState<PositionList[]>([]);
  const [editing, setEditing] = useState<{ id?: number; name: string; content: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => research.lists().then(d => setLists(d.lists)).catch(e => setError(String(e)));
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => parsePositions(editing?.content || ""), [editing?.content]);
  const roleCount = groups.reduce((n, g) => n + g.roles.length, 0);

  async function onFile(f: File) {
    if (!editing) return;
    setError(null);
    try {
      let content: string;
      if (/\.(xlsx|xls|csv)$/i.test(f.name)) {
        const wb = XLSX.read(await f.arrayBuffer());
        content = positionsFromRows(XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[wb.SheetNames[0]], { header: 1 }));
      } else {
        content = await f.text();
      }
      setEditing({ ...editing, content, name: editing.name || f.name.replace(/\.[^.]+$/, "") });
    } catch (e) {
      setError(`Could not read ${f.name}: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function save() {
    if (!editing) return;
    if (!editing.name.trim()) { setError("Give the list a name."); return; }
    if (!roleCount) { setError("Paste at least one role."); return; }
    setBusy(true); setError(null);
    try {
      if (editing.id) await research.updateList(editing.id, { name: editing.name, content: editing.content });
      else await research.saveList(editing.name, editing.content);
      setEditing(null);
      load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">Roles</h1>
          <p className="mt-1 text-sm text-slate-500">
            Saved lists of the roles you want found at a company. Pick one on the <Link className="underline" to="/outreach/research">Research</Link> page.
          </p>
        </div>
        {!editing && <button className="btn-primary" onClick={() => setEditing({ name: "", content: "" })}>+ New role list</button>}
      </div>

      {error && <div className="card p-4 text-sm text-rose-700">{error}</div>}

      {editing && (
        <div className="card space-y-3 p-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[240px] flex-1">
              <label className="mb-1 block text-xs font-medium text-slate-600">List name</label>
              <input className="w-full !text-sm !py-2 !px-3" value={editing.name} placeholder="Telco A2P buyers"
                     onChange={e => setEditing({ ...editing, name: e.target.value })} />
            </div>
            <button className="btn-secondary" onClick={() => fileRef.current?.click()}>Upload file…</button>
            <input ref={fileRef} type="file" accept=".txt,.csv,.xlsx,.xls" className="hidden"
                   onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Roles — paste here</label>
            <textarea className="h-80 w-full font-mono text-xs" value={editing.content}
                      onChange={e => setEditing({ ...editing, content: e.target.value })}
                      placeholder={"• Wholesale / Carrier\n  o Head of Wholesale\n  o Carrier Relations Manager\n• Fraud / Security\n  o Telecom Fraud Manager\n\nOr simply one role per line."} />
            <p className="mt-1 text-[11px] text-slate-500">
              {roleCount
                ? <>{roleCount} role(s) in {groups.length} group(s) — each group is one search: {groups.map(g => `${g.name} (${g.roles.length})`).join(" · ")}</>
                : "Group headings start with • or # (or end with a colon); roles under them start with o, -, * or a number. Excel/CSV: column A = group, column B = role."}
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={() => { setEditing(null); setError(null); }}>Cancel</button>
            <button className="btn-primary" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save role list"}</button>
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        {lists.length === 0
          ? <div className="p-6 text-center text-sm text-slate-500">No role lists yet.</div>
          : (
            <table className="refined">
              <thead><tr><th>Name</th><th>Roles</th><th>Groups</th><th>By</th><th>Created</th><th></th></tr></thead>
              <tbody>
                {lists.map(l => {
                  const g = parsePositions(l.content);
                  return (
                    <tr key={l.id}>
                      <td className="font-medium">{l.name}</td>
                      <td>{g.reduce((n, x) => n + x.roles.length, 0)}</td>
                      <td className="text-xs text-slate-500">{g.map(x => x.name).join(" · ")}</td>
                      <td className="text-xs text-slate-500">{l.created_by_email}</td>
                      <td className="text-xs text-slate-500">{new Date(l.created_at.replace(" ", "T") + "Z").toLocaleDateString()}</td>
                      <td className="whitespace-nowrap text-right text-xs">
                        <Link className="text-indigo-600 hover:underline" to={`/outreach/research?list=${l.id}`}>Research with it</Link>
                        <button className="ml-3 text-slate-600 hover:underline" onClick={() => setEditing({ id: l.id, name: l.name, content: l.content })}>Edit</button>
                        <button className="ml-3 text-slate-400 hover:text-rose-600" onClick={async () => {
                          if (confirm(`Delete role list "${l.name}"?`)) { await research.deleteList(l.id); load(); }
                        }}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}
